import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

import { ScrollArea, LoadingSpinner } from '@app/shared/ui/atoms/Common';
import { NotFound } from '@app/shared/ui/molecules/Common';
import { TopBar, CardsSection, AdvicesSection } from '@app/shared/ui/organisms/DailyCardResult';
import { useGetRandomCardsContentQuery } from '@app/store/slices/content/contentApi';
import { useDisplayError } from '@app/shared/hooks/useDisplayError';
import { getAdvices } from '@app/shared/utils/dailyCardResult';
import { LOCAL_STORAGE, ONE_DAY_MS, ONE_WEEK_MS } from '@app/shared/constants/common';
import { BuySettlementModal } from '@app/shared/ui/organisms/Common';
import { useSettlementGate } from '@app/shared/hooks/useSettlementGate';
import { usePageTimer } from '@app/shared/hooks/usePageTimer';

export default function DailyCardResultPage() {
  const { t } = useTranslation();
  const {
    requiredSettlement,
    isOpen,
    handlePurchase,
    isPurchasingSettlement,
    isSettlementsLoading,
  } = useSettlementGate(2, LOCAL_STORAGE.cardUsedAt, ONE_WEEK_MS);
  const { trigger } = usePageTimer(LOCAL_STORAGE.cardDailyUseAt, ONE_DAY_MS);

  useEffect(() => {
    trigger();
  }, []);

  const {
    data: randomCardsContent,
    isLoading: isRandomCardsContentLoading,
    isError: isRandomCardsContentError,
  } = useGetRandomCardsContentQuery();

  const failedToLoadRandomCardsMsg = t('errors.failed_to_load_random_cards');
  useDisplayError(isRandomCardsContentError, failedToLoadRandomCardsMsg);

  const renderContent = () => {
    if (isRandomCardsContentLoading || isSettlementsLoading) {
      return <LoadingSpinner />;
    } else if (isRandomCardsContentError) {
      return (
        <NotFound message={failedToLoadRandomCardsMsg} withLightBlueBg containerClassName="mt-5" />
      );
    }

    const advices = randomCardsContent ? getAdvices(randomCardsContent) : [];

    return (
      <>
        <CardsSection imageSrc={randomCardsContent?.icon ?? ''} />
        <AdvicesSection advices={advices} />
      </>
    );
  };

  return (
    <ScrollArea className="overflow-x-hidden">
      <TopBar />
      {renderContent()}
      {!!requiredSettlement && (
        <BuySettlementModal
          isOpen={isOpen}
          settlement={requiredSettlement}
          onPurchase={handlePurchase}
          isPurchasingSettlement={isPurchasingSettlement}
          textTKey="daily_card_result.buy_modal_text"
        />
      )}
    </ScrollArea>
  );
}
